import React, { useState, useRef } from 'react';
import {
  XMarkIcon,
  ArrowUpTrayIcon,
  DocumentArrowDownIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import inventoryApi from '../../../services/inventoryApi';

const cellText = (cell) => {
  if (!cell) return '';
  const v = cell.value;
  if (v === null || v === undefined) return '';
  if (typeof v === 'object' && v.richText) return String(cell.text || '');
  if (typeof v === 'object' && v.formula !== undefined) return String(cell.text || v.result || '');
  return String(v).trim();
};

const parsePhoneUpdateWorkbook = async (file) => {
  const buffer = await file.arrayBuffer();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);
  const sheet = workbook.worksheets[0];
  if (!sheet) return [];

  const headers = {};
  sheet.getRow(1).eachCell({ includeEmpty: true }, (cell, colNumber) => {
    headers[colNumber] = cellText(cell).toLowerCase();
  });
  const nameCol = Object.keys(headers).find((c) => headers[c].startsWith('supplier name'));
  const phoneCol = Object.keys(headers).find((c) => headers[c].startsWith('phone number'));

  const rows = [];
  const lastRow = sheet.lastRow ? sheet.lastRow.number : 1;
  for (let r = 2; r <= lastRow; r += 1) {
    const row = sheet.getRow(r);
    const supplierName = nameCol ? cellText(row.getCell(Number(nameCol))) : '';
    const phoneNumber = phoneCol ? cellText(row.getCell(Number(phoneCol))) : '';
    if (!supplierName && !phoneNumber) continue;
    rows.push({ supplierName, phoneNumber });
  }
  return rows;
};

const downloadFailedRowsReport = async (rejectedRows) => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Failed Rows');
  sheet.columns = [
    { header: 'Supplier Name', key: 'supplierName', width: 30 },
    { header: 'Phone Number', key: 'phoneNumber', width: 20 },
    { header: 'Reason', key: 'reason', width: 50 }
  ];
  rejectedRows.forEach((r) => sheet.addRow({
    supplierName: r.supplierName,
    phoneNumber: r.phoneNumber,
    reason: r.reason
  }));
  sheet.getRow(1).font = { bold: true };
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), 'supplier_phone_update_failed_rows.xlsx');
};

const SupplierBulkUpdatePhone = ({ onSuccess, onCancel }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;
    const n = selectedFile.name.toLowerCase();
    if (!n.endsWith('.xlsx')) {
      toast.error('Please select an Excel (.xlsx) file');
      return;
    }
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    setFile(selectedFile);
    setUploadResult(null);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }
    try {
      setUploading(true);
      const rows = await parsePhoneUpdateWorkbook(file);
      if (rows.length === 0) {
        toast.error('No rows found in the selected file');
        return;
      }
      const result = await inventoryApi.bulkUpdateSupplierPhoneNumbers(rows);
      const payload = result.data || result;
      setUploadResult(payload);

      const rejectedRows = (payload.results || []).filter((r) => r.status === 'rejected');
      if (rejectedRows.length > 0) {
        await downloadFailedRowsReport(rejectedRows);
        toast(`Updated ${payload.summary.updated} of ${payload.summary.total} supplier(s)`, { icon: '⚠️' });
      } else {
        toast.success(`Successfully updated ${payload.summary.updated} supplier(s)`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update supplier phone numbers');
      console.error('Supplier bulk phone update error:', err);
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('Suppliers');
      sheet.columns = [
        { header: 'Supplier Name*', key: 'supplierName', width: 30 },
        { header: 'Phone Number*', key: 'phoneNumber', width: 20 }
      ];
      sheet.addRow({ supplierName: 'ABC Suppliers', phoneNumber: '0771234567' });
      sheet.getRow(1).font = { bold: true };
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), 'supplier_bulk_update_phone_template.xlsx');
    } catch (e) {
      console.error(e);
      toast.error('Could not generate the Excel template');
    }
  };

  return (
    <div className="fixed inset-0 backdrop-blur-sm bg-black/30 z-50 flex items-center justify-center p-4">
      <div className="relative w-full max-w-3xl max-h-[90vh] bg-white shadow-2xl rounded-lg flex flex-col">
        <div className="flex-shrink-0 bg-gradient-to-r from-indigo-600 to-purple-600 px-5 py-3 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <ArrowUpTrayIcon className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-white">Bulk update phone numbers</h3>
            </div>
            {!uploading && (
              <button
                type="button"
                onClick={onCancel}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {uploading ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <svg className="animate-spin h-12 w-12 text-indigo-600 mb-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="text-sm font-medium text-gray-900">Updating supplier phone numbers…</p>
              <p className="text-xs text-gray-500 mt-1 max-w-sm">
                Large files (1000+ rows) can take a while — this keeps waiting no matter how long it takes.
                Please don't close this window.
              </p>
            </div>
          ) : !uploadResult ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Instructions
                  </h4>
                  <ul className="text-xs text-blue-800 space-y-1 ml-5 list-disc">
                    <li>Download the <strong>Excel (.xlsx) template</strong> — it has only two columns</li>
                    <li>
                      <strong>Supplier Name</strong> is matched case-insensitively against existing suppliers;{' '}
                      <strong>Phone Number</strong> must be exactly 9 digits (e.g. <em>771234567</em>) or 10 digits
                      starting with 0 (e.g. <em>0771234567</em>)
                    </li>
                    <li>A 9-digit number is automatically prefixed with <strong>0</strong> before saving</li>
                    <li>
                      Rows with an unknown supplier, multiple numbers, or an invalid format are rejected — every
                      other row is still updated
                    </li>
                    <li>Only the supplier's phone number is changed — no other field is modified</li>
                    <li>Max file size 10 MB</li>
                  </ul>
                </div>
                <button
                  type="button"
                  onClick={downloadTemplate}
                  className="w-full flex items-center justify-center px-4 py-3 border-2 border-indigo-300 border-dashed rounded-lg text-indigo-600 hover:bg-indigo-50 hover:border-indigo-400 transition-all"
                >
                  <DocumentArrowDownIcon className="w-5 h-5 mr-2" />
                  <span className="font-medium">Download Excel template (.xlsx)</span>
                </button>
              </div>

              <div className="space-y-4">
                <div
                  className={`relative border-2 border-dashed rounded-lg p-6 transition-all ${
                    dragActive ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <div className="text-center">
                    <ArrowUpTrayIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    {file ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-center space-x-2">
                          <CheckCircleIcon className="w-5 h-5 text-green-500" />
                          <span className="text-sm font-medium text-gray-900">{file.name}</span>
                        </div>
                        <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
                        <button
                          type="button"
                          onClick={() => {
                            setFile(null);
                            if (fileInputRef.current) fileInputRef.current.value = '';
                          }}
                          className="text-sm text-indigo-600 hover:text-indigo-800"
                        >
                          Remove file
                        </button>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-gray-600 mb-2">Drag and drop your Excel file here, or</p>
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                        >
                          Browse files
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleImport}
                  disabled={!file || uploading}
                  className="w-full py-2.5 px-4 rounded-md text-white font-medium bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? 'Importing…' : 'Import'}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div
                className={`p-4 rounded-lg border ${
                  uploadResult.summary.rejected > 0
                    ? 'bg-amber-50 border-amber-200'
                    : 'bg-green-50 border-green-200'
                }`}
              >
                <div className="flex items-start gap-3">
                  {uploadResult.summary.rejected > 0 ? (
                    <ExclamationTriangleIcon className="w-6 h-6 text-amber-600 flex-shrink-0" />
                  ) : (
                    <CheckCircleIcon className="w-6 h-6 text-green-600 flex-shrink-0" />
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {uploadResult.summary.updated} of {uploadResult.summary.total} supplier(s) updated
                      {uploadResult.summary.rejected > 0 && ` · ${uploadResult.summary.rejected} row(s) rejected`}
                    </p>
                  </div>
                </div>
              </div>
              {uploadResult.summary.rejected > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-semibold text-red-700">Rejected rows</p>
                    <button
                      type="button"
                      onClick={() => downloadFailedRowsReport(
                        (uploadResult.results || []).filter((r) => r.status === 'rejected')
                      )}
                      className="inline-flex items-center text-xs font-medium text-indigo-600 hover:text-indigo-800"
                    >
                      <DocumentArrowDownIcon className="w-4 h-4 mr-1" />
                      Download failed rows report
                    </button>
                  </div>
                  <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">Row</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">Supplier Name</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">Phone Number</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">Reason</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {uploadResult.results.filter((r) => r.status === 'rejected').map((r, idx) => (
                          <tr key={`rej-${r.row}-${idx}`}>
                            <td className="px-3 py-2 text-gray-600">{r.row}</td>
                            <td className="px-3 py-2 text-gray-700">{r.supplierName}</td>
                            <td className="px-3 py-2 text-gray-700">{r.phoneNumber}</td>
                            <td className="px-3 py-2 text-red-700">{r.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setUploadResult(null);
                    setFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Import another file
                </button>
                <button
                  type="button"
                  onClick={onSuccess}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupplierBulkUpdatePhone;
